/**
 * Cached default widget/chat form copy per locale.
 * Overlay only replaces stock (uncustomized) field values so merchant edits stick.
 */

/** Persisted on chat_settings / review_settings — independent of shop UI language. */
export const WIDGET_FORM_LANGUAGE_KEY = "form_language";

export const REVIEW_COPY_FIELDS = [
    "review_button_text",
    "first_review_text",
    "based_on_reviews_text",
    "popup_title_text",
    "popup_rating_excellent_text",
    "popup_rating_just_wow_text",
    "popup_rating_i_like_it_text",
    "popup_rating_i_dont_like_it_text",
    "popup_rating_i_hate_it_text",
    "review_media_gallery_title",
    "review_media_view_all_text",
    "review_trust_1_title",
    "review_trust_1_subtitle",
    "review_trust_2_title",
    "review_trust_2_subtitle",
    "review_trust_3_title",
    "review_trust_3_subtitle",
    "review_cta_title",
    "review_cta_subtitle",
    "review_list_section_title",
    "review_verified_buyer_text",
    "review_reply_from_text",
    "review_store_badge_text",
] as const;

export const CHAT_COPY_FIELDS = [
    "welcome_message",
    "info_message",
    "offline_message",
    "ai_handoff_message",
    "engage_button_label",
] as const;

/** Storefront chat widget text (chat_settings), grouped for the settings form. Translated server-side per form_language. */
export const CHAT_WIDGET_TEXT_GROUPS: ReadonlyArray<{ id: string; fields: readonly string[] }> = [
    { id: "launcher", fields: ["launcher_title", "launcher_subtitle", "home_greeting", "send_message_label", "status_online_text", "status_away_text"] },
    { id: "conversation", fields: ["input_placeholder", "just_now_text", "no_messages_title", "no_messages_text", "attachment_sent_text", "attachment_title", "attachment_device_label", "attachment_screenshot_label"] },
    { id: "suggested", fields: ["suggested_question_1", "suggested_question_2", "suggested_question_3", "suggested_question_4"] },
    { id: "visitorForm", fields: ["lead_greeting", "lead_name_label", "lead_name_placeholder", "lead_email_label", "lead_email_placeholder", "lead_invalid_email_text", "lead_submit_label"] },
    { id: "endChat", fields: ["end_chat_label", "review_title", "review_comment_placeholder", "review_submit_label", "review_thank_you_message", "chat_ended_text", "start_new_chat_label"] },
    { id: "offlineNav", fields: ["offline_heading", "leave_message_label", "nav_home_label", "nav_messages_label"] },
    { id: "help", fields: ["nav_help_label", "help_center_title", "help_center_desc", "help_search_placeholder", "help_no_results_text"] },
];

export const CHAT_WIDGET_TEXT_FIELDS: readonly string[] = CHAT_WIDGET_TEXT_GROUPS.flatMap((group) => group.fields);

export type WidgetFormCopyKind = "reviews" | "chats";

/** Backend / legacy English defaults that still count as stock. */
export const STOCK_ALIASES: Record<string, string[]> = {
    review_button_text: ["Write a Review", "Write a review"],
    first_review_text: ["Be the first to write a review", "Be the first to review this product"],
    based_on_reviews_text: ["Based on reviews", "Based on {count} reviews", "based on {count} reviews"],
    popup_title_text: ["HOW WOULD YOU RATE THIS ITEM?", "Write a Review"],
    popup_rating_excellent_text: ["EXCELLENT", "Excellent"],
    popup_rating_just_wow_text: ["JUST WOW", "Just Wow", "Great"],
    popup_rating_i_like_it_text: ["I LIKE IT", "I Like It", "Good"],
    popup_rating_i_dont_like_it_text: ["I DON'T LIKE IT", "I Don't Like It", "Fair"],
    popup_rating_i_hate_it_text: ["I HATE IT", "I Hate It", "Poor"],
    review_media_gallery_title: ["Customer photos & videos"],
    review_media_view_all_text: ["View all"],
    review_trust_1_title: ["Trusted by {count}+ customers", "Trusted by Verified Customers"],
    review_trust_1_subtitle: ["Real reviews from verified buyers"],
    review_trust_2_title: ["Honest feedback"],
    review_trust_2_subtitle: ["Help others make the right choice"],
    review_trust_3_title: ["Share your experience"],
    review_trust_3_subtitle: ["Your review helps our community"],
    review_cta_title: ["Have something to say?"],
    review_cta_subtitle: ["Share your experience and help others"],
    review_list_section_title: ["Customer reviews ({count})"],
    review_verified_buyer_text: ["Verified Buyer"],
    review_reply_from_text: ["Reply from"],
    review_store_badge_text: ["Store"],
    welcome_message: [
        "Please tell us more about you.",
        "Please provide some info so that we can reach you.",
    ],
    info_message: ["Live Chat"],
    offline_message: ["Sorry we're currently unavailable."],
    ai_handoff_message: ["Connecting you with a team member. They will reply shortly."],
    engage_button_label: ["Take over chat", "Talk to our team"],
};

export const copyCache = new Map<string, Record<string, string>>();
const stockSeen = new Map<string, Set<string>>();

export function rememberStock(copy: Record<string, string>): void {
    for (const [field, value] of Object.entries(copy)) {
        if (!value) {
            continue;
        }
        let set = stockSeen.get(field);
        if (!set) {
            set = new Set(STOCK_ALIASES[field] ?? []);
            stockSeen.set(field, set);
        }
        set.add(value);
    }
}

export function isStockCopy(field: string, value: unknown, prevCopy?: Record<string, string>): boolean {
    if (typeof value !== "string") {
        return false;
    }
    const trimmed = value.trim();
    if (trimmed === "") {
        return true;
    }
    if (prevCopy && prevCopy[field] === value) {
        return true;
    }
    const aliases = STOCK_ALIASES[field];
    if (aliases && aliases.includes(value)) {
        return true;
    }
    return stockSeen.get(field)?.has(value) === true;
}

export function overlayStockCopy<T extends Record<string, unknown>>(
    values: T,
    prevCopy: Record<string, string>,
    nextCopy: Record<string, string>
): T {
    const next = { ...values };
    for (const field of Object.keys(nextCopy)) {
        if (isStockCopy(field, values[field], prevCopy)) {
            (next as Record<string, unknown>)[field] = nextCopy[field];
        }
    }
    return next;
}

/** Starter FAQ entries in the copy pack: faq_{n}_question / faq_{n}_answer. */
export const CHAT_FAQ_DEFAULT_COUNT = 4;

/**
 * Swap starter FAQ text to another language; questions and answers the shop wrote stay as they are.
 * Matches by text, not position, so reordered starter entries still translate.
 */
export function translateStockFaq<T extends { question: string; answer: string }>(
    articles: T[],
    prevCopy: Record<string, string>,
    nextCopy: Record<string, string>
): T[] {
    const swap = (value: string, part: "question" | "answer"): string => {
        if (value.trim() === "") {
            return value;
        }
        for (let n = 1; n <= CHAT_FAQ_DEFAULT_COUNT; n++) {
            const field = `faq_${n}_${part}`;
            if (nextCopy[field] && isStockCopy(field, value, prevCopy)) {
                return nextCopy[field];
            }
        }
        return value;
    };
    return articles.map((article) => ({
        ...article,
        question: swap(article.question, "question"),
        answer: swap(article.answer, "answer"),
    }));
}

/** Prefer persisted child form_language; else fall back to shop/UI locale. */
export function resolveWidgetFormLocale(
    values: Record<string, unknown>,
    fallbackLocale = "en"
): string {
    const stored = values[WIDGET_FORM_LANGUAGE_KEY];
    if (typeof stored === "string" && stored.trim() !== "") {
        return stored.trim();
    }
    return fallbackLocale || "en";
}

/** Test helper — do not use in app code. */
export function _resetWidgetFormCopyCache(): void {
    copyCache.clear();
    stockSeen.clear();
}
